import { UseSelection } from '@/views/editor/composable/useSelection.ts'
import { ref } from 'vue'

let cachedSelectionInstance: UseSelection | null = null
let cachedKeyCountManager: ReturnType<typeof createKeyCountManager> | null = null

export function getSelectionInstance(): UseSelection {
	if (!cachedSelectionInstance) {
		cachedSelectionInstance = new UseSelection()
	}
	return cachedSelectionInstance
}

function createKeyCountManager() {
	const keyCount = ref(0)
	const isComposing = ref(false)
	const skipNextHistorySave = ref(false)
	const lastInputType = ref<'insert' | 'delete' | null>(null)

	const handleCompositionStart = () => {
		isComposing.value = true
	}

	const handleCompositionEnd = () => {
		isComposing.value = false
	}

	const shouldIncrementKeyCount = (event: KeyboardEvent): boolean => {
		if (isComposing.value) return false
		if (event.key === 'Process' || event.isComposing) return false
		
		const specialKeys = [
			'Shift',
			'Control',
			'Alt',
			'Meta',
			'CapsLock',
			'Tab',
			'Escape',
			'ArrowUp',
			'ArrowDown',
			'ArrowLeft',
			'ArrowRight',
			'Home',
			'End',
			'PageUp',
			'PageDown',
			'Insert',
			'Enter',
		]

		if (specialKeys.includes(event.key)) return false

		if (event.ctrlKey || event.metaKey || event.altKey) return false

		return true
	}

	const incrementKeyCount = () => {
		keyCount.value++
	}

	const resetKeyCount = () => {
		keyCount.value = 0
	}

	const getKeyCount = () => {
		return keyCount.value
	}
	
	const setSkipNextHistorySave = (skip: boolean) => {
		skipNextHistorySave.value = skip
	}
	
	const shouldSkipHistorySave = () => {
		if (skipNextHistorySave.value) {
			skipNextHistorySave.value = false // Reset after checking
			return true
		}
		return false
	}
	
	const getLastInputType = () => {
		return lastInputType.value
	}
	
	const setLastInputType = (type: 'insert' | 'delete' | null) => {
		lastInputType.value = type
	}

	return {
		keyCount,
		isComposing,
		handleCompositionStart,
		handleCompositionEnd,
		shouldIncrementKeyCount,
		incrementKeyCount,
		resetKeyCount,
		getKeyCount,
		setSkipNextHistorySave,
		shouldSkipHistorySave,
		getLastInputType,
		setLastInputType,
	}
}

export function getKeyCountManager() {
	if (!cachedKeyCountManager) {
		cachedKeyCountManager = createKeyCountManager()
	}
	return cachedKeyCountManager
}

export function updateSelectionSafe() {
	const selection = getSelectionInstance()
	selection.setChangeSelection()
}

export function useKeyCountManager() {
	return getKeyCountManager()
}

export function createReviewUidWatcher(blockList: any, maxWaitTime: number = 5000) {
	return new Promise<void>((resolve, reject) => {
		if (blockList.value.length > 0) {
			resolve()
			return
		}

		let resolved = false
		let timeout: ReturnType<typeof setTimeout> | null = null
		let checkInterval: ReturnType<typeof setInterval> | null = null

		const stop = () => {
			if (timeout) clearTimeout(timeout)
			if (checkInterval) clearInterval(checkInterval)
			timeout = null
			checkInterval = null
		}

		timeout = setTimeout(() => {
			if (!resolved) {
				resolved = true
				stop()
				resolve() // Resolve even on timeout
			}
		}, maxWaitTime)

		checkInterval = setInterval(() => {
			if (blockList.value.length > 0 && !resolved) {
				resolved = true
				stop()
				resolve()
			}
		}, 100)

		// Return cleanup function (can be used if needed)
		// Note: Promise doesn't support cleanup directly, but this structure is safer
	})
}
